import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Calendar, Clock, Mail, Settings, Check, X, Trash2 } from 'lucide-react';
import { 
  getAppointments, 
  updateAppointment, 
  deleteAppointment,
  getAvailability,
  updateAvailability
} from '../services/api';
import { toast } from 'sonner';

export const AppointmentsManager = () => {
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appts, avail] = await Promise.all([
        getAppointments(),
        getAvailability()
      ]);
      setAppointments(appts);
      setAvailability(avail);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await updateAppointment(appointmentId, {
        status: newStatus,
        admin_notes: adminNotes
      });
      toast.success(`Appointment ${newStatus}`);
      setSelectedAppointment(null);
      setAdminNotes('');
      loadData();
    } catch (error) {
      toast.error('Failed to update appointment');
      console.error(error);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await deleteAppointment(appointmentId);
      toast.success('Appointment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete appointment');
      console.error(error);
    }
  };

  const handleSaveAvailability = async () => {
    try {
      await updateAvailability(availability);
      toast.success('Availability updated successfully');
    } catch (error) {
      toast.error('Failed to update availability');
      console.error(error);
    }
  };


  const toggleDayAvailability = (dayIndex) => {
    const newSchedule = [...availability.schedule];
    newSchedule[dayIndex].enabled = !newSchedule[dayIndex].enabled;
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const updateTimeSlot = (dayIndex, field, value) => {
    const newSchedule = [...availability.schedule];
    if (!newSchedule[dayIndex].slots[0]) {
      newSchedule[dayIndex].slots = [{ start_time: '09:00', end_time: '17:00' }];
    }
    newSchedule[dayIndex].slots[0][field] = value;
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-green-500',
      cancelled: 'bg-red-500',
      completed: 'bg-blue-500'
    };
    return <Badge className={`${colors[status]} text-white`}>{status}</Badge>;
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <Tabs defaultValue="appointments" className="w-full">
      <TabsList className="grid w-full max-w-xl grid-cols-2">
        <TabsTrigger value="appointments">
          <Calendar className="h-4 w-4 mr-2" />
          Appointments
        </TabsTrigger>
        <TabsTrigger value="availability">
          <Clock className="h-4 w-4 mr-2" />
          Availability
        </TabsTrigger>
      </TabsList>

      {/* Appointments List */}
      <TabsContent value="appointments">
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">Appointment Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {appointments.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No appointments yet
              </p>
            ) : (
              <div className="space-y-4">
                {appointments.map((apt) => (
                  <Card key={apt.id} className="dark:bg-gray-800">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold text-lg dark:text-white">{apt.property_title}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {apt.requested_date} at {apt.requested_time}
                          </p>
                        </div>
                        {getStatusBadge(apt.status)}
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium dark:text-white">Tenant Information:</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{apt.tenant_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{apt.tenant_email}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{apt.tenant_phone}</p>
                        </div>
                        {apt.notes && (
                          <div>
                            <p className="text-sm font-medium dark:text-white">Notes:</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{apt.notes}</p>
                          </div>
                        )}
                      </div>

                      {selectedAppointment === apt.id && (
                        <div className="mb-4">
                          <Label htmlFor="admin_notes">Admin Notes (optional)</Label>
                          <Textarea
                            id="admin_notes"
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            placeholder="Add notes for the tenant..."
                            rows={2}
                          />
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {apt.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => {
                                if (selectedAppointment === apt.id) {
                                  handleStatusUpdate(apt.id, 'confirmed');
                                } else {
                                  setSelectedAppointment(apt.id);
                                }
                              }}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              {selectedAppointment === apt.id ? 'Confirm' : 'Confirm'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                if (selectedAppointment === apt.id) {
                                  handleStatusUpdate(apt.id, 'cancelled');
                                } else {
                                  setSelectedAppointment(apt.id);
                                }
                              }}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                            {selectedAppointment === apt.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedAppointment(null);
                                  setAdminNotes('');
                                }}
                              >
                                Close
                              </Button>
                            )}
                          </>
                        )}
                        {apt.status === 'confirmed' && (
                          <Button
                            size="sm"
                            className="bg-otter-green hover:bg-otter-green text-white"
                            onClick={() => handleStatusUpdate(apt.id, 'completed')}
                          >
                            Mark Complete
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-600"
                          onClick={() => handleDeleteAppointment(apt.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Availability Settings */}
      <TabsContent value="availability">
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">Set Your Availability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {availability?.schedule.map((day, index) => (
              <div key={day.day} className="flex items-center gap-4 p-4 border rounded dark:border-gray-700">
                <Checkbox
                  id={`day-${day.day}`}
                  checked={day.enabled}
                  onCheckedChange={() => toggleDayAvailability(index)}
                />
                <Label htmlFor={`day-${day.day}`} className="w-32 capitalize">
                  {day.day}
                </Label>
                {day.enabled && (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={day.slots[0]?.start_time || '09:00'}
                      onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                      className="w-32"
                    />
                    <span className="dark:text-white">to</span>
                    <Input
                      type="time"
                      value={day.slots[0]?.end_time || '17:00'}
                      onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                      className="w-32"
                    />
                  </div>
                )}
              </div>
            ))}
            <Button 
              onClick={handleSaveAvailability}
              className="bg-otter-green hover:bg-otter-green-600 text-white"
            >
              <Settings className="h-4 w-4 mr-2" />
              Save Availability
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

    </Tabs>
  );
};
